import { ModalsContext, ModalType } from 'contexts/modal';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/buttons';
import { ScCurrencyUser } from './styled';
import { AuthContext, CommonContext } from '@repo-level/core-front';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { Form, FormApi } from 'informed';
import { getCurrencyCentsAndCode } from 'helpers/wallet';
import { Select } from 'components/form/Select';

interface CurrencyApi {
  currency: string;
}

export const CurrencyUser = React.memo(() => {
  const { t } = useTranslation('glossary');

  const { wallets, changeUserWallet, user } = useContext(AuthContext);
  const { isMobile } = useContext(CommonUtilsContext);

  const { currencies } = useContext(CommonContext);
  const { handleModal } = useContext(ModalsContext);

  const formApiRef = useRef<FormApi<CurrencyApi>>();

  const optionsWallets = useMemo(() => {
    return wallets.map((wallet) => {
      const { currency, amount } = getCurrencyCentsAndCode(
        wallet.amountCents,
        wallet.currency,
        currencies
      );

      return {
        name: isMobile ? currency : `${amount} ${currency}`,
        value: wallet.currency,
      };
    });
  }, [wallets, currencies, isMobile]);

  useEffect(() => {
    if (user?.currency && formApiRef.current) {
      formApiRef.current.setValue('currency', user.currency);
    }
  }, [user?.currency]);

  const handleMobileClick = useCallback(() => {
    return handleModal(ModalType.MyCurrencies, {
      stayInSameWindow: true,
    });
  }, [handleModal]);

  const handleDesktopClick = useCallback(() => {
    return handleModal(ModalType.EditCurrencies, {
      stayInSameWindow: true,
    })();
  }, [handleModal]);

  return (
    <ScCurrencyUser>
      <h3 className="title-currency">{t('currency')}</h3>
      <div className="select-wallet">
        <Form<CurrencyApi>
          getApi={(api) => {
            formApiRef.current = api;
          }}
        >
          <Select
            field="currency"
            options={optionsWallets}
            onCustomOpen={isMobile ? handleMobileClick() : undefined}
            animatedPlaceholder={t('glossary:currency')}
            maxHeight={128}
            dropBoxShadow
            onSelect={(value: any) => {
              changeUserWallet(value);
            }}
            withBorderMode
            addons={
              <Button grayDarker small onClick={() => handleDesktopClick()}>
                {t('newCurrency')}
              </Button>
            }
          />
        </Form>
      </div>
    </ScCurrencyUser>
  );
});
